import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Public marketing layout used by /what-is-paygbot, /founder-story,
 * /blog, and individual blog articles. Mirrors the LandingPage header
 * for visual consistency and adds a footer with the internal link grid
 * required for AI-search-friendly internal linking.
 */
export default function MarketingLayout({ children }) {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-200 sticky top-0 bg-white/95 backdrop-blur z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <Link to="/" className="flex items-center space-x-2" data-testid="marketing-logo">
              <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="text-lg font-bold text-slate-900">PayGBot</span>
            </Link>
            <nav className="flex items-center space-x-2 sm:space-x-4 text-sm">
              <Link to="/what-is-paygbot" className="hidden sm:inline text-slate-600 hover:text-slate-900 font-medium">What Is PayGBot?</Link>
              <Link to="/founder-story" className="hidden sm:inline text-slate-600 hover:text-slate-900 font-medium">Founder Story</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900 font-medium">Blog</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 font-medium">Pricing</Link>
              <Link to="/login" className="text-slate-600 hover:text-slate-900 font-medium">Login</Link>
              <Link
                to="/signup"
                className="px-3 py-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
                data-testid="marketing-signup-link"
              >
                Start Free Trial
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Page content */}
      <main className="flex-1">{children}</main>

      {/* Footer with internal link grid */}
      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">P</span>
                </div>
                <span className="text-base font-bold text-slate-900">PayGBot</span>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                An AI business operating system built to help busy people build and run a business in minutes a day.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-slate-600 hover:text-slate-900">Home</Link></li>
                <li><Link to="/what-is-paygbot" className="text-slate-600 hover:text-slate-900">What Is PayGBot?</Link></li>
                <li><Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link></li>
                <li><Link to="/signup" className="text-slate-600 hover:text-slate-900">Start Free Trial</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link></li>
                <li><Link to="/blog/what-is-an-ai-business-operating-system" className="text-slate-600 hover:text-slate-900">AI Business Operating System</Link></li>
                <li><Link to="/blog/why-busy-people-need-simpler-business-systems" className="text-slate-600 hover:text-slate-900">Why Busy People Need Simpler Systems</Link></li>
                <li><Link to="/blog/how-automation-helps-families-build-income-more-flexibly" className="text-slate-600 hover:text-slate-900">Automation for Families</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-3">About</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/founder-story" className="text-slate-600 hover:text-slate-900">Founder Story</Link></li>
                <li><Link to="/blog/how-to-build-a-business-during-difficult-seasons-of-life" className="text-slate-600 hover:text-slate-900">Building During Hard Seasons</Link></li>
                <li><Link to="/blog/why-most-online-business-systems-overwhelm-beginners" className="text-slate-600 hover:text-slate-900">Why Most Systems Overwhelm Beginners</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} PayGBot. Built by Vickie Mcglawn for real people, real lives, and real businesses.
            </p>
            <p className="text-xs text-slate-500">
              Not a course. An AI business operating system.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
